import React, { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

import ValidationErrorList from './ValidationErrorList';
import { leadImportService } from '../../services/leadImportService';
import { useImportWizardStore } from '../../store/useImportWizardStore';
import type { MappingConfig, ValidationError } from '../../utils/types';

type ValidationState = 'idle' | 'loading' | 'valid' | 'invalid' | 'error';

const ValidationStep: React.FC = () => {
  const fileID = useImportWizardStore((s) => s.fileID);
  const mappingConfig = useImportWizardStore((s) => s.mappingConfig);
  const setStep = useImportWizardStore((s) => s.setStep);

  const [state, setState] = useState<ValidationState>('idle');
  const [errors, setErrors] = useState<ValidationError[]>([]);

  useEffect(() => {
    if (!fileID || Object.keys(mappingConfig).length === 0) return;
    if (state !== 'idle') return;

    setState('loading');

    leadImportService
      .validateMapping(fileID, mappingConfig as MappingConfig)
      .then((response: any) => {
        const validationErrors = response?.errors || [];
        if (response?.valid === true || validationErrors.length === 0) {
          setState('valid');
          setErrors([]);
        } else {
          setState('invalid');
          setErrors(validationErrors);
        }
      })
      .catch(() => {
        setState('error');
      });
  }, [fileID, mappingConfig, state]);

  return (
    <Box data-testid="validation-step">
      <Typography variant="h6" gutterBottom>
        Validation
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Verifying your mapping configuration against the uploaded file.
      </Typography>

      <Box aria-live="polite">
        {state === 'loading' && (
          <Box display="flex" alignItems="center" gap={2} sx={{ my: 4 }}>
            <CircularProgress size={24} />
            <Typography variant="body1">
              Validating mapping configuration…
            </Typography>
          </Box>
        )}

        {state === 'invalid' && (
          <ValidationErrorList
            errors={errors}
            onNavigateToSection={() => setStep('mapping')}
          />
        )}

        {state === 'error' && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to validate mapping configuration. Please try again.
          </Alert>
        )}

        {state === 'valid' && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Mapping configuration is valid. You can now start the import.
          </Alert>
        )}
      </Box>

      <Box display="flex" gap={2} sx={{ mt: 3 }}>
        <Button variant="outlined" onClick={() => setStep('mapping')}>
          Back to Mapping
        </Button>

        {state === 'valid' && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => setStep('progress')}
          >
            Start Import
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default ValidationStep;
